import React, { useEffect } from 'react';

const TermsPage: React.FC = () => {
  useEffect(() => {
    document.title = 'Terms and Conditions | InTimelog';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white pt-44 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-navy-900 mb-2">InTimeLog Terms & Conditions</h1>
        <p className="text-sm text-navy-600">Effective Date: May 30, 2026</p>
        <p className="text-sm text-navy-600 mb-8">Last Updated: May 30, 2026</p>

        <div className="prose prose-navy max-w-none space-y-6 text-navy-700">
          <p>
            These Terms & Conditions ("Terms") govern all transportation services and communications provided by InTimeLog ("Company," "we," "our," or "us"). By using or accessing our services, dispatch systems, or communications, you agree to these Terms.
          </p>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">1. Transportation Services</h2>
            <p>InTimeLog provides professional transportation and logistics coordination services including, but not limited to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Airport transportation</li>
              <li>Airline crew transportation</li>
              <li>Hotel transportation</li>
              <li>Corporate transportation</li>
              <li>Event transportation</li>
              <li>Point-to-point transportation</li>
              <li>Fleet and dispatch coordination services</li>
            </ul>
            <p>Service availability may vary by location, staffing, weather conditions, airport operations, traffic conditions, vehicle availability, and regulatory requirements.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">2. Right to Refuse Service</h2>
            <p>We reserve the right to refuse, suspend, or terminate service at any time for reasons including but not limited to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Unsafe behavior</li>
              <li>Harassment or abuse toward drivers or staff</li>
              <li>Illegal activity</li>
              <li>Intoxication</li>
              <li>Threatening conduct</li>
              <li>Violation of these Terms</li>
              <li>Excessive vehicle occupancy</li>
              <li>Failure to comply with applicable laws or driver instructions</li>
            </ul>
            <p>No refunds are guaranteed for rides terminated due to violations of these Terms.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">3. Pricing & Billing</h2>
            <p>All transportation services are provided under authorized contract accounts. Pricing is based on pre-negotiated contract rates between InTimeLog and the contracting entity (hotel, corporation, or airline).</p>
            <p>Contract rates may vary based on:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Distance and route</li>
              <li>Airport location</li>
              <li>Vehicle type and availability</li>
              <li>Service level agreement</li>
              <li>Volume commitment</li>
              <li>Time of day</li>
              <li>Special events and holidays</li>
            </ul>
            <p>Additional fees may apply for:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Waiting time beyond contracted allowance</li>
              <li>Extra stops not included in original request</li>
              <li>Excessive delays caused by passenger</li>
              <li>Vehicle cleaning or damage</li>
              <li>Tolls and airport fees</li>
              <li>Administrative modifications to scheduled services</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">4. Account-Based Service</h2>
            <p>InTimeLog provides transportation services exclusively through authorized contract accounts, including:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Hotel Partnership Accounts:</strong> Services coordinated through contracted hotel relationships for guest transportation</li>
              <li><strong>Corporate Accounts:</strong> Dedicated transportation services for business clients under negotiated agreements</li>
              <li><strong>Airline Operational Contracts:</strong> Crew logistics and airline-specific transportation managed through dispatch coordination</li>
            </ul>
            <p>Service terms, pricing, availability, and operational procedures are defined by the specific contract agreement with each account.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">5. Dispatch-Controlled Operations</h2>
            <p>All transportation services are subject to real-time dispatch management and operational control. InTimeLog dispatch reserves the right to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Modify Vehicle Assignments:</strong> Reassign drivers or vehicles based on operational needs, fleet availability, or unforeseen circumstances</li>
              <li><strong>Adjust Vehicle Types:</strong> Substitute vehicles of similar or greater capacity when necessary for operational efficiency or safety</li>
              <li><strong>Adjust Pickup Timing:</strong> Modify scheduled pickup times to accommodate traffic, weather, flight changes, driver availability, or operational requirements</li>
              <li><strong>Consolidate Trips:</strong> Combine multiple passengers or stops when operationally appropriate for airline crew or corporate accounts</li>
              <li><strong>Route Optimization:</strong> Determine the most efficient routing based on real-time conditions</li>
            </ul>
            <br/>
            <p>These operational adjustments are essential for airline crew logistics, corporate account management, and maintaining service reliability across all operations. Customers will be notified of significant changes when operationally feasible.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">6. Airline Crew Transportation</h2>
            <p><strong>Contract-Based Coordination:</strong> Airline crew transportation services are provided exclusively through contracted dispatch operations and airline operational channels.</p>
            <p>Airline crew logistics are managed through:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Direct airline contracts and operational agreements</li>
              <li>Dedicated dispatch coordination channels</li>
              <li>Hotel-based crew manifest systems</li>
              <li>Real-time operational communication protocols</li>
            </ul>
            <p>All airline crew transportation is billed directly to the contracting airline under the terms of the operational agreement.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">7. Service Requests & Confirmation</h2>
            <p><strong>Contract-Based Authorization:</strong> All transportation service requests must be submitted through authorized contract account channels (hotel concierge, corporate travel desk, or airline dispatch coordination).</p>
            <p>Service requests are subject to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Account Verification:</strong> Confirmation that the request originates from an authorized contract account</li>
              <li><strong>Vehicle Availability:</strong> Confirmation that appropriate vehicles and drivers are available for the requested service</li>
              <li><strong>Route Feasibility:</strong> Verification that the requested route can be serviced within operational parameters</li>
              <li><strong>Demand Management:</strong> Evaluation of overall fleet demand during the requested time period</li>
            </ul>
            <p><strong>Service confirmation is provided through your account coordinator or InTimeLog dispatch.</strong></p>

            <h3 className="font-semibold text-navy-900 mt-4 mb-2">High-Demand Periods</h3>
            <p>Service availability is especially limited during:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>High-demand periods (holidays, major events, severe weather)</li>
              <li>Peak airport travel times</li>
              <li>Large group or corporate events</li>
              <li>Times when airline crew operations have priority scheduling</li>
            </ul>
            <p>Priority scheduling is given to contracted airline crew operations and pre-scheduled corporate account services during high-demand periods.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">8. Grace Period & Waiting Time</h2>
            <h3 className="text-lg font-semibold text-navy-900 mb-2">Standard Pickups</h3>
            <p>We provide a 10-minute grace period after the scheduled pickup time.</p>
            <p>After the grace period expires:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Waiting charges may apply</li>
              <li>Drivers may leave the location</li>
              <li>The ride may be considered a no-show</li>
            </ul>
            <h3 className="text-lg font-semibold text-navy-900 mb-2 mt-4">Airport Arrivals</h3>
            <p>For commercial airline arrivals:</p>
            <p>A 30-minute grace period begins after the actual flight landing time unless otherwise agreed.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">9. Flight Monitoring</h2>
            <p>When flight information is provided, we may attempt to monitor flight status for operational purposes. However:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Customers remain responsible for providing accurate flight information.</li>
              <li>We do not guarantee real-time flight tracking accuracy.</li>
              <li>We are not responsible for delays caused by inaccurate or unavailable airline data.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">10. Delays & Force Majeure</h2>
            <p>We are not responsible for service interruptions, delays, cancellations, or failures caused by events beyond our reasonable control, including:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Traffic</li>
              <li>Road closures</li>
              <li>Weather</li>
              <li>Airport congestion</li>
              <li>Airline disruptions</li>
              <li>Mechanical failures</li>
              <li>Government actions</li>
              <li>Civil unrest</li>
              <li>Internet or GPS outages</li>
              <li>Third-party software failures</li>
              <li>Labor shortages</li>
              <li>Emergencies</li>
            </ul>
            <p>Estimated pickup and arrival times are not guarantees.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">11. Passenger Conduct</h2>
            <p>Passengers must behave safely and respectfully at all times.</p>
            <p>The following are strictly prohibited:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Smoking or vaping</li>
              <li>Illegal drug use</li>
              <li>Open containers where prohibited by law</li>
              <li>Violence or threats</li>
              <li>Damage to vehicles</li>
              <li>Harassment</li>
              <li>Possession of illegal substances or weapons</li>
            </ul>
            <p>Drivers may immediately terminate rides if safety concerns arise.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">12. Drug & Alcohol Policy</h2>
            <p>InTimeLog maintains a strict zero-tolerance policy regarding illegal drugs and intoxication.</p>
            <p>Employees, contractors, and drivers are prohibited from:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Operating vehicles under the influence</li>
              <li>Possessing illegal substances while working</li>
              <li>Consuming intoxicating substances during active service</li>
            </ul>
            <p>We reserve the right to remove drivers or passengers from service if impairment is suspected.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">13. Vehicle Capacity & Safety</h2>
            <p>Passengers may not exceed the legal seating capacity of any vehicle.</p>
            <p>Drivers may refuse service if:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Seatbelt requirements cannot be met</li>
              <li>Passenger count exceeds capacity</li>
              <li>Cargo creates unsafe conditions</li>
            </ul>
            <p>Children must comply with all applicable child restraint laws.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">14. Minors, Child Seats & Accessibility</h2>
            <p>InTimeLog does not provide transportation services for unaccompanied minors.</p>
            <p>All passengers under the age of 18 must be accompanied by a parent, legal guardian, or authorized adult.</p>
            <p>Customers are responsible for providing and properly installing any legally required child restraint or booster seats. Drivers are not authorized or certified to install child safety seats.</p>
            <p>Drivers may refuse service if:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>A required child restraint is unavailable</li>
              <li>A child cannot be transported legally or safely</li>
              <li>The reservation involves an unaccompanied minor</li>
            </ul>
            <p>InTimeLog is not a medical transportation provider and does not provide specialized medical, wheelchair, stretcher, or ADA paratransit transportation services unless explicitly agreed in writing.</p>
            <p>Special accommodation requests are subject to availability and operational limitations.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">15. Personal Property</h2>
            <p>Passengers are solely responsible for their belongings.</p>
            <p>InTimeLog is not liable for:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Lost items</li>
              <li>Stolen items</li>
              <li>Damaged property</li>
              <li>Forgotten belongings</li>
            </ul>
            <p>Recovered items may be returned at the customer's expense.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">16. Cleaning & Damage Fees</h2>
            <p>Passengers are financially responsible for damage caused during service.</p>
            <p>Charges may include:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Vomit or bodily fluid cleanup</li>
              <li>Excessive mess</li>
              <li>Smoke odor removal</li>
              <li>Interior damage</li>
              <li>Upholstery damage</li>
              <li>Electronic damage</li>
              <li>Broken accessories</li>
              <li>Property destruction</li>
            </ul>
            <p>Minimum cleaning or damage fees may range from $50 to $500 or more depending on severity.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">17. Cancellations & No-Shows</h2>
            <p>Cancellation policies may vary by account type and contract agreement.</p>
            <p>Unless otherwise specified in your contract:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Service requests canceled within 24 hours may incur charges.</li>
              <li>Group or event transportation may require longer notice.</li>
              <li>No-shows may be charged the full contracted service amount.</li>
            </ul>
            <p>Repeated cancellations or abuse of service requests may result in account review or restrictions.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">18. Payments & Billing</h2>

            <h3 className="font-semibold text-navy-900 mt-4 mb-2">Hotel Partnership Accounts</h3>
            <p>Hotel partnership accounts are billed according to the terms of the specific hotel contract agreement. Payment terms, invoicing frequency, and billing procedures are established in the hotel service agreement.</p>

            <h3 className="font-semibold text-navy-900 mt-4 mb-2">Corporate Accounts</h3>
            <p>Corporate clients operate under invoicing terms agreed upon in their service contracts. Invoices are issued according to the contracted billing cycle and payment terms.</p>

            <h3 className="font-semibold text-navy-900 mt-4 mb-2">Airline Operational Contracts</h3>
            <p>Airline crew transportation services are billed directly to the contracting airline according to the operational agreement and negotiated payment terms.</p>

            <h3 className="font-semibold text-navy-900 mt-4 mb-2">Authorized Charges</h3>
            <p>Contract accounts may be charged for:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Transportation service fees</li>
              <li>Waiting time beyond contracted allowance</li>
              <li>Damage fees</li>
              <li>Tolls and airport fees</li>
              <li>Applicable taxes</li>
              <li>Administrative fees for service modifications</li>
            </ul>
            <p>Invoices not paid within the contracted payment terms may incur late fees and collection actions.</p>
            <p>Disputes regarding valid completed services may result in account review or service suspension.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">19. Communications Consent</h2>
            <p>If you provide your phone number, email address, or contact information to InTimeLog (such as for lost and found requests or emergency coordination), you consent to receive:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Follow-up communications related to your inquiry</li>
              <li>Lost and found notifications</li>
              <li>Emergency coordination messages from dispatch</li>
              <li>SMS messages or phone calls as necessary to resolve your request</li>
            </ul>
            <p>Message and data rates may apply.</p>
            <p>Contact information is collected only when voluntarily provided for specific support purposes and is used solely to address your inquiry or coordinate service-related matters.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">20. GPS & Tracking</h2>
            <p>Vehicles, drivers, mobile applications, and dispatch systems may utilize:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>GPS tracking</li>
              <li>Geolocation services</li>
              <li>Route monitoring</li>
              <li>Driver telematics</li>
              <li>Operational analytics</li>
            </ul>
            <p>This information may be used for:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Dispatching</li>
              <li>Safety</li>
              <li>Fraud prevention</li>
              <li>Customer support</li>
              <li>Service optimization</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">21. Limitation of Liability</h2>
            <p>To the fullest extent permitted by law, InTimeLog shall not be liable for:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Indirect damages</li>
              <li>Consequential damages</li>
              <li>Lost profits</li>
              <li>Missed flights</li>
              <li>Missed meetings</li>
              <li>Business interruptions</li>
              <li>Emotional distress</li>
              <li>Third-party actions</li>
              <li>Delays beyond our control</li>
            </ul>
            <p>Our total liability shall not exceed the amount paid for the affected service.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">22. Indemnification</h2>
            <p>You agree to indemnify and hold harmless InTimeLog and its officers, employees, contractors, affiliates, and partners from any claims, damages, liabilities, losses, or expenses arising from:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Your misuse of services</li>
              <li>Violation of these Terms</li>
              <li>Passenger misconduct</li>
              <li>Property damage</li>
              <li>Illegal conduct</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">23. Account Suspension & Fraud</h2>
            <p>We reserve the right to suspend or terminate accounts for:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Fraudulent activity</li>
              <li>Chargeback abuse</li>
              <li>Fake reservations</li>
              <li>Abuse toward staff</li>
              <li>Security concerns</li>
              <li>Violation of policies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">24. Privacy</h2>
            <p>Your use of our services is also governed by our Privacy Policy.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">25. Governing Law</h2>
            <p>These Terms are governed by the laws of the State of Texas without regard to conflict-of-law principles.</p>
            <p>Any disputes shall be resolved in courts located in Texas.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-navy-900 mb-3">26. Changes to Terms</h2>
            <p>We may update these Terms at any time without prior notice.</p>
            <p>Continued use of our services constitutes acceptance of revised Terms.</p>
          </section>

          <div className="mt-12 pt-6 border-t border-navy-200">
            <h3 className="text-lg font-semibold text-navy-900 mb-3">Contact Information</h3>
            <p>
              For questions regarding these Terms, contact:<br />
              <strong>InTimeLog</strong><br />
              Dallas, Texas, USA<br />
              Email: <a href="mailto:support@intimelog.com" className="text-primary-600 hover:underline">support@intimelog.com</a><br />
              Phone: <a href="tel:+14695632674" className="text-primary-600 hover:underline">(469) 563-2674</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
